import { StatusInfo } from '../status-info.model';
import { SubscriptionStatus, CourtesyStatus, CheckInStatus } from '../enums';
import { APP_COLORS } from './colors';

/**
 * Configuration for subscription status display
 * Maps each status to its corresponding icon, color, and tooltip (in Spanish for UI)
 */

export const SUBSCRIPTION_STATUS_CONFIG: Record<SubscriptionStatus, StatusInfo> = {
  [SubscriptionStatus.CANCELED]: {
    icon: 'block',
    color: APP_COLORS.ERROR,
    tooltip: 'Cancelada',
  },
  [SubscriptionStatus.EXPIRING_SOON]: {
    icon: 'warning',
    color: APP_COLORS.WARNING,
    tooltip: 'Próxima a vencer',
  },
  [SubscriptionStatus.ACTIVE]: {
    icon: 'check_circle',
    color: APP_COLORS.SUCCESS,
    tooltip: 'Vigente',
  },
  [SubscriptionStatus.EXPIRED]: {
    icon: 'cancel',
    color: APP_COLORS.DISABLED,
    tooltip: 'Vencida',
  },
  [SubscriptionStatus.UNKNOWN]: {
    icon: 'help',
    color: APP_COLORS.NEUTRAL,
    tooltip: 'Estado desconocido',
  },
  [SubscriptionStatus.PROGRAMMED]: {
    icon: 'schedule',
    color: APP_COLORS.INFO,
    tooltip: 'Programada',
  },
  [SubscriptionStatus.NONE]: {
    icon: 'person_off',
    color: APP_COLORS.NEUTRAL,
    tooltip: 'Sin suscripción',
  },
  [SubscriptionStatus.SESSION]: {
    icon: 'event_available',
    color: APP_COLORS.INFO,
    tooltip: 'Sesión',
  },
};

/**
 * CSS class of the badge that renders each subscription status in the catalogs
 */
export const SUBSCRIPTION_STATUS_BADGE_CLASS: Record<SubscriptionStatus, string> = {
  [SubscriptionStatus.ACTIVE]: 'active',
  [SubscriptionStatus.EXPIRING_SOON]: 'expiring',
  [SubscriptionStatus.PROGRAMMED]: 'programmed',
  [SubscriptionStatus.CANCELED]: 'canceled',
  [SubscriptionStatus.EXPIRED]: 'expired',
  [SubscriptionStatus.UNKNOWN]: 'unknown',
  [SubscriptionStatus.NONE]: 'unknown',
  [SubscriptionStatus.SESSION]: 'session',
};

/**
 * Configuration for courtesy status display
 * Maps each status to its corresponding icon, color, and tooltip (in Spanish for UI)
 */
export const COURTESY_STATUS_CONFIG: Record<CourtesyStatus, StatusInfo> = {
  [CourtesyStatus.ACTIVE]: {
    icon: 'check_circle',
    color: APP_COLORS.SUCCESS,
    tooltip: 'Vigente',
  },
  [CourtesyStatus.EXPIRING_SOON]: {
    icon: 'warning',
    color: APP_COLORS.WARNING,
    tooltip: 'Próxima a vencer',
  },
  [CourtesyStatus.UNKNOWN]: {
    icon: 'help',
    color: APP_COLORS.NEUTRAL,
    tooltip: 'Estado desconocido',
  },
  [CourtesyStatus.CANCELED]: {
    icon: 'block',
    color: APP_COLORS.ERROR,
    tooltip: 'Cancelada',
  },
  [CourtesyStatus.EXPIRED]: {
    icon: 'cancel',
    color: APP_COLORS.DISABLED,
    tooltip: 'Vencida',
  },
};

/**
 * CSS class of the badge that renders each courtesy status in the catalogs
 */
export const COURTESY_STATUS_BADGE_CLASS: Record<CourtesyStatus, string> = {
  [CourtesyStatus.ACTIVE]: 'active',
  [CourtesyStatus.EXPIRING_SOON]: 'expiring',
  [CourtesyStatus.CANCELED]: 'canceled',
  [CourtesyStatus.EXPIRED]: 'expired',
  [CourtesyStatus.UNKNOWN]: 'unknown',
};

/**
 * Configuration for check-in status display
 * Maps each status to its corresponding icon, color, and tooltip (in Spanish for UI)
 */
export const CHECK_IN_STATUS_CONFIG: Record<CheckInStatus, StatusInfo> = {
  [CheckInStatus.ACTIVE]: {
    icon: 'check_circle',
    color: APP_COLORS.SUCCESS,
    tooltip: 'Vigente',
    tone: 'success',
  },
  [CheckInStatus.EXPIRING_SOON]: {
    icon: 'warning',
    color: APP_COLORS.WARNING,
    tooltip: 'Próxima a vencer',
    tone: 'warning',
  },
  // Expired and no-subscription block the entrance just like a cancellation, so
  // they share the red tone: the receptionist reads the card from across the room.
  [CheckInStatus.EXPIRED]: {
    icon: 'cancel',
    color: APP_COLORS.ERROR,
    tooltip: 'Vencido',
    tone: 'danger',
  },
  [CheckInStatus.CANCELED]: {
    icon: 'block',
    color: APP_COLORS.ERROR,
    tooltip: 'Cancelada',
    tone: 'danger',
  },
  [CheckInStatus.NO_SUBSCRIPTION]: {
    icon: 'person_off',
    color: APP_COLORS.ERROR,
    tooltip: 'Sin suscripción',
    tone: 'danger',
  },
  [CheckInStatus.UNKNOWN]: {
    icon: 'help',
    color: APP_COLORS.NEUTRAL,
    tooltip: 'Estado desconocido',
    tone: 'neutral',
  },
  [CheckInStatus.MEMBER_NOT_FOUND]: {
    icon: 'person_off',
    color: APP_COLORS.ERROR,
    tooltip: 'Socio no encontrado',
    tone: 'danger',
  },
  [CheckInStatus.FINGERPRINT_NOT_RECOGNIZED]: {
    icon: 'fingerprint',
    color: APP_COLORS.ERROR,
    tooltip: 'Huella no reconocida',
    tone: 'danger',
  },
};
